import { useSelector } from '@xstate/react';
import cn from 'classnames';
import { duration } from 'dayjs';
import { useMemo } from 'react';

import { QuestionOrder } from '@webapp/common/components/misc';
import { CssPageBlock } from '@webapp/common/components/survey-custom';
import { TimeLeft } from '@webapp/common/components/time-left';
import { partsToSeconds } from '@webapp/common/lib/date';
import { createTextStyle, setHtml } from '@webapp/common/lib/ui';
import type { InfoModel } from '@webapp/common/resources/entities/info';
import { QuestionWithoutHeader } from '@webapp/common/resources/survey';

import type { QuestionContext } from '@webapp/survey/src/machine/question-machine';
import { usePipingAnswersReplacer } from '@webapp/survey/src/machine/use-piping-answers-replacer';

import { styleToQuestionPosition } from '../lib';
import css from '../question.css';

export const Header: VFC<{
    mark: ReactNode;
    question: QuestionContext;
    info: InfoModel;
    readOnly: boolean;
    twisterReadOnly?: boolean;
    showOrder: boolean;
    showRequired: boolean;
    withError: boolean;
}> = ({
    info: {
        design: {
            fonts: {
                elementColor,
                questionTextBold,
                questionTextColor,
                questionTextItalic,
                questionTextSize,
                questionTextThrough,
                questionTextUnderline,
            },
            view: { questionBlockPosition },
        },
    },
    mark,
    question,
    readOnly,
    twisterReadOnly,
    showOrder,
    showRequired,
    withError,
}) => {
    const {
        order,
        params: { timer: paramTimer, hours, minutes, seconds },
        timer,
        title,
        type,
    } = question;
    const pipingAnswersReplacer = usePipingAnswersReplacer();
    const showTitle = !QuestionWithoutHeader.has(type);
    const questionPositionClass = questionBlockPosition ? styleToQuestionPosition[questionBlockPosition] : '';
    const headerTitleStyles: CSSProperties = createTextStyle({
        underline: questionTextUnderline,
        through: questionTextThrough,
        bold: questionTextBold,
        italic: questionTextItalic,
        color: questionTextColor,
        size: questionTextSize,
    });
    const showTimer = Boolean(!readOnly && !twisterReadOnly && paramTimer);
    // Когда актора таймера ещё нет (например, FIRST_CLICK до открытия
    // изображения), показываем полное настроенное время, а не 00:00:00.
    const timerLeft = useSelector(timer, (timer) =>
        timer
            ? duration(Math.max(0, timer.context.duration * 1000 - timer.context.timeAgo))
            : duration(partsToSeconds(hours, minutes, seconds) * 1000),
    );

    const orderStyle: CSSProperties = useMemo(
        () => ({
            color: elementColor,
        }),
        [elementColor],
    );

    return (
        <div className={cn(CssPageBlock.QUESTION_HEADER, css.questionHeader, { [css.withTimer]: paramTimer })}>
            {showTitle && (
                <div className={cn(css.header, questionPositionClass)} style={headerTitleStyles}>
                    {showOrder && (
                        <QuestionOrder invalid={withError} order={order} style={orderStyle} className={css.order} />
                    )}
                    <div className={cn(CssPageBlock.QUESTION_TITLE, css.title)}>
                        <span className={css.inner} {...setHtml(pipingAnswersReplacer(title))} />
                        {showRequired && <span className={css.requiredTitle}>{mark}</span>}
                    </div>
                    {showTimer && <TimeLeft className={css.timer} color={elementColor} value={timerLeft} />}
                </div>
            )}
        </div>
    );
};
